"use client";

import { cn } from "@/lib/utils";
import Autoplay from "embla-carousel-autoplay";
import useEmblaCarousel from "embla-carousel-react";
import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";
import { MediaCarouselFooter } from "./MediaCarouselFooter";
import { MediaCarouselVideoPlayer } from "./MediaCarouselVideoPlayer";
import type { MediaCarouselProps } from "./types";
import { isVideoUrl } from "./utils";

const carouselClasses =
  "relative w-full aspect-video overflow-hidden rounded-[var(--border-radius-x6,12px)] max-h-[70vh]";
const viewportClasses = "overflow-hidden w-full h-full";
const containerClasses = "flex h-full";
const slideClasses =
  "relative flex-[0_0_100%] min-w-0 aspect-video rounded-[var(--border-radius-x6,12px)]";

export const MediaCarousel = ({
  urls,
  onSlideChange,
  className,
}: MediaCarouselProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isAnyVideoPlaying, setIsAnyVideoPlaying] = useState(false);
  const videoDomRefs = useRef<Record<string, HTMLVideoElement | null>>({});

  const autoplayPlugin = useRef(
    Autoplay({
      delay: 5000,
      stopOnInteraction: false,
      stopOnMouseEnter: true,
      stopOnFocusIn: true,
    }),
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: "start",
    },
    [autoplayPlugin.current],
  );

  const scrollTo = useCallback(
    (index: number) => {
      emblaApi?.scrollTo(index);
      const url = urls[index];
      if (url && onSlideChange) {
        onSlideChange(url);
      }
    },
    [emblaApi, urls, onSlideChange],
  );

  const handleVideoEnd = useCallback(() => {
    if (!emblaApi) return;

    const currentIndex = emblaApi.selectedScrollSnap();
    const nextIndex = (currentIndex + 1) % urls.length;
    emblaApi.scrollTo(nextIndex);
  }, [emblaApi, urls.length]);

  const handleVideoPlayingStateChange = useCallback((isPlaying: boolean) => {
    setIsAnyVideoPlaying(isPlaying);

    if (isPlaying) {
      // Pause autoplay when any video starts playing
      autoplayPlugin.current?.stop();
    } else {
      // Resume autoplay when no videos are playing
      autoplayPlugin.current?.play();
    }
  }, []);

  const controlCarouselVideosPlayback = useCallback(async (index: number) => {
    for (const videoElement of Object.values(videoDomRefs.current)) {
      if (videoElement && !videoElement.paused && videoElement.pause) {
        videoElement.pause();
      }
    }

    const activeVideo = videoDomRefs.current[`slide-${index}-video`];
    if (activeVideo?.paused) {
      try {
        await activeVideo.play();
        autoplayPlugin.current?.stop();
      } catch (err) {
        autoplayPlugin.current?.play();
      }
    } else {
      autoplayPlugin.current?.play();
    }
  }, []);

  const onSelect = useCallback(async () => {
    if (!emblaApi) return;

    const index = emblaApi.selectedScrollSnap();
    setSelectedIndex(index);
    await controlCarouselVideosPlayback(index);
  }, [emblaApi, controlCarouselVideosPlayback]);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect();
    emblaApi.on("select", onSelect);

    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSelect]);

  if (!urls || urls.length === 0) {
    return null;
  }

  return (
    <div className={cn("w-full max-w-full", className)}>
      {/* Main Carousel */}
      <div className={carouselClasses}>
        <div className={viewportClasses} ref={emblaRef}>
          <div className={containerClasses}>
            {urls.map((url, index) => {
              const isVideo = isVideoUrl(url);
              const isActive = selectedIndex === index;

              return (
                <div key={url} className={slideClasses}>
                  {isVideo ? (
                    <MediaCarouselVideoPlayer
                      url={url}
                      isActive={isActive}
                      videoDomRef={(el) => {
                        videoDomRefs.current[`slide-${index}-video`] = el;
                      }}
                      onVideoEnd={handleVideoEnd}
                      onPlayingStateChange={handleVideoPlayingStateChange}
                    />
                  ) : (
                    <Image
                      src={url}
                      alt={`Slide ${index + 1}`}
                      fill
                      className="object-contain"
                      priority={index === 0}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <MediaCarouselFooter
        urls={urls}
        selectedIndex={selectedIndex}
        onSlideSelect={scrollTo}
      />
    </div>
  );
};
